import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { OneTimeReveal } from "@/components/OneTimeReveal";
import { Bell, CheckCheck } from "lucide-react";

// SEC-05: notification bodies are server-generated but historically carried HTML fragments
// (<b>…</b>) with interpolated values. Rendering them as raw HTML (dangerouslySetInnerHTML)
// was a stored-XSS sink. We now render as PLAIN TEXT — strip any tags and let React escape,
// so no notification content can ever execute, regardless of what a call site puts in a line.
const asText = (s) => String(s || "").replace(/<[^>]*>/g, "");

export function NotificationBell() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [open, setOpen] = useState(false);
  const [reveal, setReveal] = useState(null);
  const [data, setData] = useState({ unread: 0, items: [] });
  const ref = useRef(null);

  const load = () => api.get("/notifications/inbox").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc); return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const markAll = async () => { await api.post("/notifications/read-all").catch(() => {}); load(); };
  const go = (it) => { setOpen(false); markAll(); if (it.link) navigate(it.link); };

  const [busyId, setBusyId] = useState(null);
  const deviceAction = async (it, choice) => {
    const target = it.meta?.target_email || "this account";
    const isMine = !!me?.email && target === me.email;
    // "Not me" on YOUR OWN account is a legitimate thing to want — somebody else got in as you —
    // so it is not blocked. But it resets your password AND your 2FA and signs you out on the
    // spot, so the warning has to say what you will need to get back in. The temporary password it
    // returns used to be shown in a native alert you could not copy text out of; it now goes
    // through the shared one-time reveal, which will not close until you confirm you have saved it.
    if (choice === "not_me" && !window.confirm(isMine
      ? `Lock YOUR OWN account?\n\nThis resets YOUR password AND YOUR 2FA and signs you out everywhere, immediately.\n\n• You'll get a one-time temporary password — SAVE IT before closing the box, it is the only way back in.\n• You'll scan a new QR code with your authenticator app.\n• Your printed backup codes will NOT get you in — they replace the 6-digit code, not the password.\n\nThis can't be undone.`
      : `Lock ${target}?\n\nThis resets their password AND 2FA and immediately signs them out everywhere. You'll get a one-time temporary password to hand off. This can't be undone.`)) return;
    setBusyId(it.id);
    try {
      const { data } = await api.post(`/notifications/${it.id}/new-device-action`, { choice });
      if (choice === "not_me" && data.temp_password) {
        setReveal({ target, value: data.temp_password });
        return;  // NO refresh behind an open reveal — it runs when the box is closed.
      }
      load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Action failed");
    } finally { setBusyId(null); }
  };
  const isReview = (it) => it.meta?.action === "new_device_review";

  return (
    <div className="relative" ref={ref}>
      <OneTimeReveal
        open={!!reveal}
        title="Account locked — temporary password"
        intro={reveal ? `${reveal.target} must sign in with this and set their own password, then re-enroll 2FA. It is shown once.` : ""}
        value={reveal?.value}
        onClose={() => { setReveal(null); load(); }}
        testid="device-lock-reveal" />
      <button data-testid="notif-bell" onClick={() => { setOpen((o) => !o); if (!open) load(); }} className="relative text-slate-600 hover:text-[#B01E28] p-1.5 rounded-lg hover:bg-slate-50" aria-label="Notifications">
        <Bell className="w-5 h-5" />
        {data.unread > 0 && <span data-testid="notif-bell-badge" className="absolute -top-0.5 -right-0.5 bg-[#B01E28] text-white text-[9px] font-mono font-bold rounded-full px-1 min-w-[16px] text-center leading-[16px]">{data.unread > 9 ? "9+" : data.unread}</span>}
      </button>
      {open && (
        <div data-testid="notif-dropdown" className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-white border border-slate-200 rounded-xl shadow-lg z-50 overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
            <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Notifications</span>
            {data.items.length > 0 && <button data-testid="notif-mark-all" onClick={markAll} className="text-[11px] text-[#E0781A] hover:underline flex items-center gap-1"><CheckCheck className="w-3 h-3" />Mark all read</button>}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {data.items.length === 0 ? <div className="p-6 text-center text-xs text-slate-400">No notifications</div>
              : data.items.map((it) => (
                isReview(it) ? (
                  <div key={it.id} data-testid={`notif-item-${it.id}`} className={`px-3 py-2.5 border-b border-slate-50 ${!it.read ? "bg-amber-50/40" : ""}`}>
                    <div className="flex items-start gap-2">
                      {!it.read && <span className="w-1.5 h-1.5 rounded-full bg-[#B01E28] mt-1.5 shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-800">{it.title}</div>
                        <div className="text-xs text-slate-500 line-clamp-3">{asText(it.body)}</div>
                        {it.resolved ? (
                          <div data-testid={`notif-resolved-${it.id}`} className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${it.resolution === "not_me" ? "bg-red-50 text-[#B01E28]" : "bg-green-50 text-[#2E7D32]"}`}>
                            {it.resolution === "not_me" ? "Locked — password + 2FA reset" : "Confirmed — this was me"}
                          </div>
                        ) : (
                          <div className="flex gap-2 mt-2">
                            <button data-testid={`notif-was-me-${it.id}`} disabled={busyId === it.id} onClick={() => deviceAction(it, "me")} className="text-[11px] font-medium px-2.5 py-1 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50">This was me</button>
                            <button data-testid={`notif-not-me-${it.id}`} disabled={busyId === it.id} onClick={() => deviceAction(it, "not_me")} className="text-[11px] font-medium px-2.5 py-1 rounded-lg bg-[#B01E28] text-white hover:bg-[#8f1820] disabled:opacity-50">Not me — lock account</button>
                          </div>
                        )}
                        <div className="font-mono text-[10px] text-slate-300 mt-1">{(it.created_at || "").slice(0, 16).replace("T", " ")}</div>
                      </div>
                    </div>
                  </div>
                ) : (
                <button key={it.id} data-testid={`notif-item-${it.id}`} onClick={() => go(it)} className={`w-full text-left px-3 py-2.5 border-b border-slate-50 hover:bg-slate-50 ${!it.read ? "bg-amber-50/40" : ""}`}>
                  <div className="flex items-start gap-2">
                    {!it.read && <span className="w-1.5 h-1.5 rounded-full bg-[#B01E28] mt-1.5 shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{it.title}</div>
                      <div className="text-xs text-slate-500 line-clamp-2">{asText(it.body)}</div>
                      <div className="font-mono text-[10px] text-slate-300 mt-0.5">{(it.created_at || "").slice(0, 16).replace("T", " ")}</div>
                    </div>
                  </div>
                </button>
                )
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
